import type { Game } from '@game/types'
import type { UseHeroGamesOptions, UseHeroGamesReturn } from '@nimbus/types'
import { computed, toValue, useGames, useNimbusConfig } from '#imports'
import { heroRotatorGames } from '@nimbus/utils/heroRotator'

// One search under one key for every hero game in the brand config, so the layout can
// start it in its first data round and the rotator and LobbyHero join that request.
export function useHeroGames(options: UseHeroGamesOptions = {}): UseHeroGamesReturn {
    const { components } = useNimbusConfig()

    const rotatorSlugs = heroRotatorGames(components?.HeroRotator).map(game => game.slug)
    const lobbyHeroSlugs = (components?.LobbyHero?.messages ?? [])
        .map(message => message.gameSlug)
        .filter((slug): slug is string => !!slug)
    const slugs = [...new Set([...rotatorSlugs, ...lobbyHeroSlugs])]

    // Hidden-tile games stay in the result: a LobbyHero slide may feature one, as the per-slug
    // endpoint allowed. The rotator drops them itself.
    const query = useGames({
        filter: { slugs, exclude_hidden_tile_types: false },
        params: { page: 1, page_size: Math.max(slugs.length, 1) },
        key: 'hero-games',
        lazy: false,
        enabled: () => slugs.length > 0 && toValue(options.enabled ?? true),
    })

    const bySlug = computed(() => new Map<string, Game>((query.data.value?.data ?? []).map(game => [game.slug, game])))

    return query.enrich({ bySlug })
}
